/**
 * EmailDomainSection - Manages allowed email domains for an organization
 *
 * For OrgAdmin users only. Allows adding/removing email domains
 * that are permitted to join the organization.
 * On mobile, this section is read-only with a prompt to use web for editing.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { isWeb as checkIsWeb } from '../../util/platform';
import { View, StyleSheet, Pressable, ActivityIndicator, TextInput } from 'react-native'
import { Feather } from '@expo/vector-icons';
import { heightPercentageToDP as hp } from 'react-native-responsive-screen';

import CustomText from '../CustomText';
import AppStyles from '../../AppStyles';
import Util from '../../util/util';
import { useTranslation } from '../../i18n';

/**
 * @param {string} organization - Organization name
 * @param {boolean} readOnly - If true, show read-only view (for mobile)
 */
const EmailDomainSection = ({ organization, readOnly = false }) => {
  const { t } = useTranslation(['admin', 'common']);

  const [domains, setDomains] = useState([]);
  const [inheritedDomains, setInheritedDomains] = useState([]);
  const [parentOrg, setParentOrg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Add domain state
  const [newDomain, setNewDomain] = useState('');
  const [saving, setSaving] = useState(false);
  const [inputError, setInputError] = useState(null);

  const isWeb = checkIsWeb();
  const canEdit = !readOnly && isWeb;

  // Domain validation regex (must match backend)
  // Supports optional wildcard prefix (*.), then standard domain format
  const DOMAIN_REGEX = /^(\*\.)?[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)*$/i;

  const fetchDomains = useCallback(async () => {
    if (!organization) return;

    setLoading(true);
    setError(null);

    try {
      const response = await Util.get('Resty', '/items/organization/email-domains', {
        queryParams: { organization },
      });

      if (response?.data?.allowed_email_domains) {
        setDomains(response.data.allowed_email_domains);
      } else {
        setDomains([]);
      }

      // Store inherited domains from parent organization
      if (response?.data?.inherited_domains) {
        setInheritedDomains(response.data.inherited_domains);
      } else {
        setInheritedDomains([]);
      }

      if (response?.data?.parent_organization) {
        setParentOrg(response.data.parent_organization);
      } else {
        setParentOrg(null);
      }
    } catch (err) {
      console.error('Error fetching email domains:', err);
      setError(t('emailDomains.loadFailed'));
      setDomains([]);
      setInheritedDomains([]);
      setParentOrg(null);
    } finally {
      setLoading(false);
    }
  }, [organization]);

  useEffect(() => {
    fetchDomains();
  }, [fetchDomains]);

  const validateDomain = (domain) => {
    const trimmed = domain.trim().toLowerCase();
    if (!trimmed) {
      return t('emailDomains.required');
    }
    if (!DOMAIN_REGEX.test(trimmed)) {
      return t('emailDomains.invalid');
    }
    if (domains.includes(trimmed)) {
      return t('emailDomains.duplicate');
    }
    return null;
  };

  const handleAddDomain = async () => {
    const validationError = validateDomain(newDomain);
    if (validationError) {
      setInputError(validationError);
      return;
    }

    setSaving(true);
    setInputError(null);

    const updatedDomains = [...domains, newDomain.trim().toLowerCase()];

    try {
      const response = await Util.patch('Resty', '/items/organization/email-domains', {
        body: {
          organization,
          allowed_email_domains: updatedDomains,
        },
      });

      if (response?.error) {
        setInputError(response.error);
        return;
      }

      setDomains(response?.data?.allowed_email_domains || updatedDomains);
      setNewDomain('');
    } catch (err) {
      console.error('Error adding domain:', err);
      setInputError(err.message || t('emailDomains.addFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveDomain = async (domainToRemove) => {
    setSaving(true);
    setInputError(null);

    const updatedDomains = domains.filter((d) => d !== domainToRemove);

    try {
      const response = await Util.patch('Resty', '/items/organization/email-domains', {
        body: {
          organization,
          allowed_email_domains: updatedDomains,
        },
      });

      if (response?.error) {
        setError(response.error);
        return;
      }

      setDomains(response?.data?.allowed_email_domains || updatedDomains);
    } catch (err) {
      console.error('Error removing domain:', err);
      setError(err.message || t('emailDomains.removeFailed'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <View style={styles.container}>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="small" color={AppStyles.colorSet.brandPrimary} />
          <CustomText style={styles.loadingText} size={14} color={AppStyles.colorSet.textSecondary}>
            {t('emailDomains.loading')}
          </CustomText>
        </View>
      </View>
    );
  }

  if (error) {
    return (
      <View style={styles.container}>
        <View style={styles.errorContainer}>
          <Feather name="alert-circle" size={20} color={AppStyles.colorSet.error} />
          <CustomText style={styles.errorText} size={14} color={AppStyles.colorSet.error}>
            {error}
          </CustomText>
          <Pressable onPress={fetchDomains} style={styles.retryButton}>
            <CustomText size={13} color={AppStyles.colorSet.brandPrimary}>
              {t('emailDomains.retry')}
            </CustomText>
          </Pressable>
        </View>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <CustomText
        style={styles.heading}
        font="semiBold"
        size={18}
        color={AppStyles.colorSet.textPrimary}
      >
        {t('emailDomains.title')}
      </CustomText>
      <CustomText
        style={styles.explanationText}
        font="regular"
        size={14}
        color={AppStyles.colorSet.textSecondary}
      >
        {t('emailDomains.description')}
        Use *.domain.com to allow all subdomains.
      </CustomText>

      {/* Inherited domains from parent (read-only) */}
      {inheritedDomains.length > 0 && (
        <View style={styles.inheritedSection}>
          <View style={styles.inheritedHeader}>
            <Feather name="lock" size={14} color={AppStyles.colorSet.textMuted} />
            <CustomText style={styles.inheritedLabel} size={13} color={AppStyles.colorSet.textMuted}>
              {t('emailDomains.inheritedFrom', { organization: parentOrg })}
            </CustomText>
          </View>
          <View style={styles.domainList}>
            {inheritedDomains.map((domain) => (
              <View key={`inherited-${domain}`} style={styles.inheritedDomainItem}>
                <Feather name="at-sign" size={16} color={AppStyles.colorSet.textMuted} />
                <CustomText style={styles.domainText} size={14} color={AppStyles.colorSet.textMuted}>
                  {domain}
                </CustomText>
              </View>
            ))}
          </View>
        </View>
      )}

      {/* Empty state - only show when no inherited AND no own domains */}
      {domains.length === 0 && inheritedDomains.length === 0 && (
        <View style={styles.emptyState}>
          <Feather name="globe" size={32} color={AppStyles.colorSet.textMuted} />
          <CustomText style={styles.emptyText} size={14} color={AppStyles.colorSet.textSecondary}>
            {t('emailDomains.noRestrictions')}
          </CustomText>
        </View>
      )}

      {/* This organization's domains (editable) */}
      {(domains.length > 0 || inheritedDomains.length > 0) && (
        <View style={styles.ownDomainsSection}>
          {inheritedDomains.length > 0 && (
            <View style={styles.sectionHeader}>
              <Feather name="edit-2" size={14} color={AppStyles.colorSet.textSecondary} />
              <CustomText style={styles.sectionLabel} size={13} color={AppStyles.colorSet.textSecondary}>
                {t('emailDomains.thisOrganization')}
              </CustomText>
            </View>
          )}
          {domains.length > 0 ? (
            <View style={styles.domainList}>
              {domains.map((domain) => (
                <View key={domain} style={styles.domainItem}>
                  <Feather name="at-sign" size={16} color={AppStyles.colorSet.textSecondary} />
                  <CustomText style={styles.domainText} size={14} color={AppStyles.colorSet.textPrimary}>
                    {domain}
                  </CustomText>
                  {canEdit && (
                    <Pressable
                      style={({ hovered, focused }) => [
                        styles.removeButton,
                        { cursor: 'pointer' },
                        (hovered || focused) && styles.removeButtonHovered,
                      ]}
                      onPress={() => handleRemoveDomain(domain)}
                      disabled={saving}
                      accessibilityLabel={`Remove ${domain}`}
                      accessibilityRole="button"
                    >
                      <Feather name="x" size={18} color={AppStyles.colorSet.error} />
                    </Pressable>
                  )}
                </View>
              ))}
            </View>
          ) : (
            <CustomText style={styles.noOwnDomainsText} size={13} color={AppStyles.colorSet.textMuted}>
              {t('emailDomains.noAdditional')}
            </CustomText>
          )}
        </View>
      )}

      {/* Add domain form */}
      {canEdit && (
        <View style={styles.addForm}>
          <View style={styles.inputRow}>
            <TextInput
              style={[styles.input, inputError && styles.inputError]}
              value={newDomain}
              onChangeText={(text) => {
                setNewDomain(text);
                setInputError(null);
              }}
              placeholder={t('emailDomains.placeholder')}
              placeholderTextColor={AppStyles.colorSet.textMuted}
              autoCapitalize="none"
              autoCorrect={false}
              editable={!saving}
              onSubmitEditing={handleAddDomain}
            />
            <Pressable
              style={({ pressed, hovered, focused }) => [
                styles.addButton,
                { cursor: 'pointer' },
                newDomain.trim() && (hovered || focused) && styles.addButtonHovered,
                !newDomain.trim() && styles.addButtonDisabled,
                pressed && styles.addButtonPressed,
              ]}
              onPress={handleAddDomain}
              disabled={saving || !newDomain.trim()}
              accessibilityLabel={t('emailDomains.addLabel')}
              accessibilityRole="button"
            >
              {saving ? (
                <ActivityIndicator size="small" color={AppStyles.colorSet.textPrimary} />
              ) : (
                <Feather name="plus" size={20} color={AppStyles.colorSet.textPrimary} />
              )}
            </Pressable>
          </View>

          {inputError && (
            <View style={styles.inputErrorContainer}>
              <Feather name="alert-circle" size={14} color={AppStyles.colorSet.error} />
              <CustomText style={styles.inputErrorText} size={13} color={AppStyles.colorSet.error}>
                {inputError}
              </CustomText>
            </View>
          )}
        </View>
      )}

      {/* Mobile read-only notice */}
      {readOnly && !isWeb && (
        <View style={styles.webOnlyNotice}>
          <Feather name="monitor" size={16} color={AppStyles.colorSet.textMuted} />
          <CustomText style={styles.webOnlyText} size={13} color={AppStyles.colorSet.textMuted}>
            {t('emailDomains.editOnWeb')}
          </CustomText>
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    paddingHorizontal: 12,
  },
  loadingContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: hp(3),
  },
  loadingText: {
    marginLeft: 10,
  },
  errorContainer: {
    alignItems: 'center',
    paddingVertical: hp(2),
    backgroundColor: '#FEF2F2',
    borderRadius: 8,
    padding: 16,
  },
  errorText: {
    marginTop: 8,
    textAlign: 'center',
  },
  retryButton: {
    marginTop: 12,
    padding: 8,
  },
  heading: {
    marginTop: hp(1),
    marginBottom: hp(0.5),
  },
  explanationText: {
    marginBottom: hp(1.5),
    lineHeight: 20,
  },
  emptyState: {
    alignItems: 'center',
    paddingVertical: hp(3),
    backgroundColor: AppStyles.colorSet.inputBackground,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#E5E5E5',
    borderStyle: 'dashed',
  },
  emptyText: {
    marginTop: 8,
  },
  inheritedSection: {
    marginBottom: hp(1.5),
  },
  inheritedHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  inheritedLabel: {
    marginLeft: 6,
  },
  inheritedDomainItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 10,
    paddingHorizontal: 16,
    backgroundColor: '#F3F4F6',
    borderRadius: 8,
    marginBottom: 8,
    borderWidth: 1,
    borderColor: '#E5E5E5',
    borderStyle: 'dashed',
  },
  ownDomainsSection: {
    marginBottom: hp(1),
  },
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  sectionLabel: {
    marginLeft: 6,
  },
  noOwnDomainsText: {
    paddingVertical: 10,
    paddingHorizontal: 16,
    fontStyle: 'italic',
  },
  domainList: {
    marginBottom: hp(1),
  },
  domainItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 10,
    paddingHorizontal: 16,
    backgroundColor: AppStyles.colorSet.inputBackground,
    borderRadius: 8,
    marginBottom: 8,
    borderWidth: 1,
    borderColor: '#E5E5E5',
  },
  domainText: {
    flex: 1,
    marginLeft: 12,
  },
  removeButton: {
    padding: 4,
    borderRadius: 6,
  },
  removeButtonHovered: {
    backgroundColor: AppStyles.stateSet.hover,
  },
  addForm: {
    marginTop: hp(1),
  },
  inputRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  input: {
    flex: 1,
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#E5E5E5',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 14,
    color: AppStyles.colorSet.textPrimary,
  },
  inputError: {
    borderColor: AppStyles.colorSet.error,
  },
  addButton: {
    backgroundColor: AppStyles.colorSet.brandPrimary,
    padding: 10,
    borderRadius: 8,
    justifyContent: 'center',
    alignItems: 'center',
  },
  addButtonDisabled: {
    backgroundColor: AppStyles.colorSet.textMuted,
    opacity: 0.5,
  },
  addButtonHovered: {
    opacity: 0.92,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.2,
    shadowRadius: 6,
    elevation: 2,
  },
  addButtonPressed: {
    opacity: 0.8,
  },
  inputErrorContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 8,
  },
  inputErrorText: {
    marginLeft: 6,
  },
  webOnlyNotice: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 12,
    backgroundColor: '#F9FAFB',
    borderRadius: 8,
    marginTop: hp(1),
  },
  webOnlyText: {
    marginLeft: 8,
  },
});

export default EmailDomainSection;
